import { useEffect, Component } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import Navbar from "@/components/Navbar";
import CartDrawer from "@/components/CartDrawer";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import AuthCallback from "@/components/AuthCallback";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import FAQ from "@/pages/FAQ";
import FastFood from "@/pages/FastFood";
import ProductDetail from "@/pages/ProductDetail";
import QuoteCheckout from "@/pages/QuoteCheckout";
import AdminQuotes from "@/pages/AdminQuotes";
import "@/App.css";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(err) {
    return { err };
  }
  render() {
    if (this.state.err) {
      return (
        <div className="min-h-screen bg-forest text-kraft font-display flex items-center justify-center p-8 text-center">
          <div>
            <p className="text-2xl font-semibold mb-2">A flap came loose.</p>
            <p className="text-kraft/60 text-sm">Please refresh the page.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  // Emergent Google OAuth returns here with #session_id=... — handle it before any route/auth check
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/shop" element={<Shop />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/fast-food" element={<FastFood />} />
      <Route path="/product/:slug" element={<ProductDetail />} />
      <Route path="/quote" element={<QuoteCheckout />} />
      <Route path="/admin" element={<AdminQuotes />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <div className="bg-forest min-h-screen">
            <Navbar />
            <AppRoutes />
            <Footer />
            <CartDrawer />
            <WhatsAppButton />
          </div>
        </BrowserRouter>
        <Toaster position="top-center" richColors theme="dark" />
      </CartProvider>
    </ErrorBoundary>
  );
}

export default App;
